/**
 * Process Steps Block
 *
 * Features:
 * - Desktop: All step cards visible, click nodes for smooth scroll
 * - Mobile: Only active step card visible, click to switch
 * - Applies color themes dynamically from step data
 *
 * @param element - The container element with data-component="ProcessSteps"
 * @returns Cleanup function
 */
export function ProcessSteps(element) {
    const isMobile = () => window.innerWidth < 768;
    // DOM elements
    const stepNodes = element.querySelectorAll("[data-step-node]");
    const stepButtons = element.querySelectorAll("[data-step-button]");
    const mobileCardContainer = element.querySelector("[data-active-step-card]");
    if (stepNodes.length === 0)
        return () => { };
    /**
     * Clone and display the active step card on mobile
     */
    const cloneActiveCard = (stepIndex) => {
        if (!mobileCardContainer || !isMobile())
            return;
        const stepNode = element.querySelector(`[data-step-node][data-step-index="${stepIndex}"]`);
        if (!stepNode) {
            console.warn(`ProcessSteps: Step node not found for index ${stepIndex}`);
            return;
        }
        const stepItem = stepNode.querySelector("[data-step-item]");
        if (!stepItem) {
            console.warn(`ProcessSteps: Step item not found in step node ${stepIndex}`);
            return;
        }
        // Clear existing content
        mobileCardContainer.innerHTML = "";
        // Clone the step item
        const clone = stepItem.cloneNode(true);
        mobileCardContainer.appendChild(clone);
    };
    /**
     * Set active state for a specific step
     */
    const setActiveStep = (stepIndex) => {
        // Update all nodes
        stepNodes.forEach((node, idx) => {
            const isActive = idx + 1 === stepIndex;
            node.setAttribute("data-active", isActive ? "true" : "false");
        });
        // Update mobile view
        if (isMobile()) {
            cloneActiveCard(stepIndex);
        }
    };
    /**
     * Handle step node click
     */
    const handleStepClick = (stepIndex) => {
        setActiveStep(stepIndex);
        // On desktop, smooth scroll to the corresponding node (they're inline now)
        if (!isMobile()) {
            const targetNode = element.querySelector(`[data-step-node][data-step-index="${stepIndex}"]`);
            if (targetNode) {
                targetNode.scrollIntoView({ behavior: "smooth", block: "nearest", inline: "center" });
            }
        }
    };
    /**
     * Handle keyboard navigation (Enter/Space on step buttons)
     */
    const handleKeyPress = (event, stepIndex) => {
        if (event.key === "Enter" || event.key === " ") {
            event.preventDefault();
            handleStepClick(stepIndex);
        }
    };
    /**
     * Initialize the component
     */
    const initialize = () => {
        // Set initial active step (first step)
        setActiveStep(1);
        // Attach click handlers to step buttons
        stepButtons.forEach((button, idx) => {
            const stepIdx = idx + 1;
            button.addEventListener("click", () => handleStepClick(stepIdx));
            button.addEventListener("keypress", (e) => handleKeyPress(e, stepIdx));
        });
    };
    /**
     * Handle window resize
     */
    let resizeTimeout;
    const handleResize = () => {
        clearTimeout(resizeTimeout);
        resizeTimeout = window.setTimeout(() => {
            // Re-initialize on layout change
            const activeNode = element.querySelector('[data-step-node][data-active="true"]');
            if (activeNode) {
                const activeIndex = parseInt(activeNode.getAttribute("data-step-index") || "1", 10);
                setActiveStep(activeIndex);
            }
        }, 250);
    };
    // Initialize
    initialize();
    // Add resize listener
    window.addEventListener("resize", handleResize);
    // Cleanup function
    return () => {
        window.removeEventListener("resize", handleResize);
        clearTimeout(resizeTimeout);
    };
}
